import { useEffect } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  DateInput,
  DeleteButton,
  Edit,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  required,
  useGetOne,
  useNotify,
  useRecordContext,
} from "react-admin";
import { TextField as MuiTextField } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { Col, Row } from "../../components/grid.component";
import { Text } from "../../components/typography.component";
import { formatCPForCNPJ, normalizeDocumentForApi } from "../../utils/mask.util";
import { regexs } from "../../utils/regexs.util";

const paymentMethodChoices = [
  { id: "Pix", name: "Pix" },
  { id: "Depósito em conta", name: "Depósito em conta" },
  { id: "Cartão de Crédito", name: "Cartão de Crédito" },
  { id: "Cartão de Débito", name: "Cartão de Débito" },
  { id: "Boleto", name: "Boleto" },
  { id: "Cheque", name: "Cheque" },
  { id: "Empenho", name: "Empenho" },
];

const statusChoices = [
  { id: "Pagamento pendente", name: "Pagamento pendente" },
  { id: "Aguardando", name: "Aguardando" },
  { id: "Concluído", name: "Concluído" },
  { id: "Cancelado", name: "Cancelado" },
  { id: "Reembolsado", name: "Reembolsado" },
  { id: "Malsucedido", name: "Malsucedido" },
];

const Title = ({ record }: any) => <Text gutterBottom={false}>{record?.transactionId || "Pedido"}</Text>;

const parseLocaleNumber = (value: unknown, fallback = 0) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  const normalized = trimmed.includes(",") && trimmed.includes(".")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed.replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const UserDisplay = () => {
  const record = useRecordContext();
  const userId = record?.userId ?? record?.user_id;
  const { data: user, isLoading } = useGetOne("users", { id: userId }, { enabled: !!userId });

  return (
    <MuiTextField
      label="Usuário"
      value={isLoading ? "Carregando..." : user?.name || ""}
      disabled
      fullWidth
      variant="filled"
    />
  );
};

const UserRegistrationDisplay = () => {
  const record = useRecordContext();
  const userId = record?.userId ?? record?.user_id;
  const { data: user, isLoading } = useGetOne("users", { id: userId }, { enabled: !!userId });

  return (
    <MuiTextField
      label="CPF/CNPJ"
      value={isLoading ? "Carregando..." : user?.registration || ""}
      disabled
      fullWidth
      variant="filled"
    />
  );
};

const TotalAmountAutoDisplay = () => {
  const { control, setValue } = useFormContext();
  const items = useWatch({ control, name: "items" }) || [];
  const totalDiscount = useWatch({ control, name: "totalDiscount" });
  const totalDiscountSnake = useWatch({ control, name: "total_discount" });

  const grossTotal = Array.isArray(items)
    ? items.reduce((acc: number, item: any) => {
        const quantity = parseLocaleNumber(item?.quantity, 0);
        const price = parseLocaleNumber(item?.price, 0);
        return acc + price * quantity;
      }, 0)
    : 0;

  const discountSource =
    totalDiscount !== undefined && totalDiscount !== null && totalDiscount !== ""
      ? totalDiscount
      : totalDiscountSnake;
  const discount = parseLocaleNumber(discountSource, 0);
  const calculated = Math.max(0, Number((grossTotal - discount).toFixed(2)));

  useEffect(() => {
    setValue("totalAmount", calculated, { shouldDirty: true, shouldValidate: true });
    setValue("total_amount", calculated, { shouldDirty: true, shouldValidate: true });
  }, [calculated, setValue]);

  return (
    <>
      <TextInput source="totalAmount" sx={{ display: "none" }} />
      <MuiTextField
        label="Valor total"
        value={calculated.toFixed(2)}
        disabled
        fullWidth
        variant="filled"
        helperText="Calculado automaticamente: (soma dos itens) - desconto"
      />
    </>
  );
};

const validateOrder = (values: any) => {
  const errors: any = {};

  if (!values.userId && !values.user_id) {
    errors.userId = "Usuário é obrigatório";
  }
  if (!values.orderDate && !values.order_date) {
    errors.orderDate = "Data do pedido é obrigatória";
  }
  if (!values.externalId && !values.external_id) {
    errors.externalId = "Identificador externo é obrigatório";
  }
  if (!values.billingName && !values.billing_name) {
    errors.billingName = "Nome do titular é obrigatório";
  }
  const billingDoc = values.billingDoc ?? values.billing_doc;
  if (!billingDoc) {
    errors.billingDoc = "CPF/CNPJ do titular é obrigatório";
  } else if (!regexs.CPJ_AND_CNPJ_REGEX.test(String(billingDoc))) {
    errors.billingDoc = "CPF ou CNPJ Inválido";
  }

  if (!values.billingStreet && !values?.billing_address?.street) {
    errors.billingStreet = "Rua é obrigatória";
  }
  if (!values.billingNumber && !values?.billing_address?.number) {
    errors.billingNumber = "Número é obrigatório";
  }
  if (!values.billingCity && !values?.billing_address?.city) {
    errors.billingCity = "Cidade é obrigatória";
  }
  if (!values.billingState && !values?.billing_address?.state) {
    errors.billingState = "UF é obrigatória";
  }
  if (!values.billingPostalCode && !values?.billing_address?.postal_code) {
    errors.billingPostalCode = "CEP é obrigatório";
  }
  if (!values.billingCountry && !values?.billing_address?.country) {
    errors.billingCountry = "País é obrigatório";
  }

  if (!values.status) {
    errors.status = "Status é obrigatório";
  }

  if (!Array.isArray(values.items) || values.items.length === 0) {
    errors.items = "Adicione pelo menos um item";
  } else {
    const itemErrors = values.items.map((item: any) => {
      const current: any = {};
      if (!item?.product_id && !item?.productId) {
        current.product_id = "Produto é obrigatório";
      }
      if (parseLocaleNumber(item?.quantity, 0) < 1) {
        current.quantity = "Quantidade deve ser maior ou igual a 1";
      }
      if (parseLocaleNumber(item?.price, -1) < 0) {
        current.price = "Preço deve ser maior ou igual a 0";
      }
      return Object.keys(current).length ? current : undefined;
    });

    if (itemErrors.some(Boolean)) {
      errors.items = itemErrors;
    }
  }

  return errors;
};

const OrderEditToolbar = () => {
  const notify = useNotify();

  return (
    <Toolbar sx={{ justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationMode="pessimistic"
        confirmTitle="Excluir pedido"
        confirmContent="Tem certeza que deseja excluir este pedido? Esta ação não poderá ser desfeita."
        mutationOptions={{
          onError: (error: any) => {
            const raw = error?.body?.message ?? error?.message ?? "Erro ao excluir pedido";
            const message = Array.isArray(raw) ? raw.join(", ") : String(raw);
            notify(message, { type: "error" });
          },
        }}
      />
    </Toolbar>
  );
};

export const OrderEdit = (props: any) => {
  useEffect(() => {
    document.title = "Pedido";
  }, []);

  return (
    <Edit
      title={<Title />}
      transform={(data: any) => ({
        user_id: data.userId ?? data.user_id,
        external_id: data.externalId ?? data.external_id,
        order_date: data.orderDate ?? data.order_date,
        payment_method: data.paymentMethod ?? data.payment_method,
        total_discount: parseLocaleNumber(data.totalDiscount ?? data.total_discount, 0),
        status: data.status,
        total_amount: parseLocaleNumber(data.totalAmount ?? data.total_amount, 0),
        billing_name: data.billingName ?? data.billing_name,
        billing_doc: normalizeDocumentForApi(data.billingDoc ?? data.billing_doc),
        items: (data.items || []).map((item: any) => ({
          product_id: item.product_id ?? item.productId,
          quantity: parseLocaleNumber(item.quantity, 1),
          price: parseLocaleNumber(item.price, 0),
        })),
        billing_address: {
          street: data.billingStreet,
          number: data.billingNumber,
          complement: data.billingComplement,
          neighborhood: data.billingNeighborhood,
          city: data.billingCity,
          state: data.billingState,
          postal_code: data.billingPostalCode,
          country: data.billingCountry,
        },
      })}
      {...props}
    >
      <SimpleForm toolbar={<OrderEditToolbar />} validate={validateOrder}>
        <Text variant="subtitle1" display="block">
          Dados do pedido
        </Text>
        <Row spacing={3}>
          <Col sm={6}>
            <UserDisplay />
          </Col>
          <Col sm={6}>
            <UserRegistrationDisplay />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={6}>
            <TextInput source="externalId" label="Identificador externo" fullWidth validate={required("Identificador externo é obrigatório")} />
          </Col>
          <Col sm={6}>
            <TextInput source="billingName" label="Nome do titular" fullWidth validate={required("Nome do titular é obrigatório")} />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={6}>
            <TextInput
              source="billingDoc"
              label="CPF/CNPJ do titular"
              fullWidth
              parse={(value: string) => value?.toUpperCase()}
              format={(value: string) => formatCPForCNPJ(value)}
              validate={[
                required("CPF/CNPJ do titular é obrigatório"),
                (value: string) => (!value || regexs.CPJ_AND_CNPJ_REGEX.test(value) ? undefined : "CPF ou CNPJ Inválido"),
              ]}
            />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <DateInput source="orderDate" label="Data do pedido" fullWidth disabled />
          </Col>
          <Col sm={4}>
            <SelectInput source="paymentMethod" label="Método de pagamento" choices={paymentMethodChoices} fullWidth disabled />
          </Col>
          <Col sm={4}>
            <SelectInput source="status" label="Status" choices={statusChoices} fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <NumberInput source="totalDiscount" label="Desconto total" fullWidth min={0} />
          </Col>
          <Col sm={8}>
            <TotalAmountAutoDisplay />
          </Col>
        </Row>

        <Text variant="subtitle1" display="block">
          Itens do pedido
        </Text>
        <ArrayInput source="items">
          <SimpleFormIterator inline>
            <ReferenceInput label="Produto" reference="products" source="product_id" perPage={100} sx={{ minWidth: 420 }}>
              <AutocompleteInput
                optionText="name"
                filterToQuery={(searchText: string) => ({ name: searchText })}
                shouldRenderSuggestions={(val: string) => val.trim().length > 1}
                noOptionsText="Digite para buscar produtos"
                validate={required("Produto é obrigatório")}
              />
            </ReferenceInput>
            <NumberInput source="quantity" label="Quantidade" min={1} validate={required("Quantidade é obrigatória")} sx={{ width: 140 }} />
            <NumberInput source="price" label="Preço" min={0} validate={required("Preço é obrigatório")} sx={{ width: 180 }} />
          </SimpleFormIterator>
        </ArrayInput>

        <Text variant="subtitle1" display="block">
          Endereço de cobrança
        </Text>
        <Row spacing={3}>
          <Col sm={8}>
            <TextInput source="billingStreet" label="Rua" fullWidth validate={required("Rua é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingNumber" label="Número" fullWidth validate={required("Número é obrigatório")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingComplement" label="Complemento" fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <TextInput source="billingNeighborhood" label="Bairro" fullWidth />
          </Col>
          <Col sm={4}>
            <TextInput source="billingCity" label="Cidade" fullWidth validate={required("Cidade é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingState" label="UF" fullWidth validate={required("UF é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingPostalCode" label="CEP" fullWidth validate={required("CEP é obrigatório")} />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <TextInput source="billingCountry" label="País" fullWidth validate={required("País é obrigatório")} />
          </Col>
        </Row>
      </SimpleForm>
    </Edit>
  );
};
